import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const OrderTable = ({ 
  orders, 
  onViewDetails, 
  onTrackOrder, 
  onReorder, 
  onDownloadInvoice, 
  onReturnItems 
}) => {
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'bg-success text-success-foreground';
      case 'shipped':
        return 'bg-primary text-primary-foreground';
      case 'processing':
        return 'bg-warning text-warning-foreground';
      case 'cancelled':
        return 'bg-error text-error-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  if (orders.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Orders Found</h3>
        <p className="text-muted-foreground">
          No orders match your current filters. Try adjusting your search criteria.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="text-left p-4 font-medium text-foreground">Order</th>
              <th className="text-left p-4 font-medium text-foreground">Date</th>
              <th className="text-left p-4 font-medium text-foreground">Status</th>
              <th className="text-left p-4 font-medium text-foreground">Items</th>
              <th className="text-left p-4 font-medium text-foreground">Total</th>
              <th className="text-left p-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, index) => (
              <tr 
                key={order.id} 
                className={`border-b border-border hover:bg-muted/30 transition-colors ${
                  index === orders.length - 1 ? 'border-b-0' : ''
                }`}
              >
                {/* Order Info */}
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Image
                        src={order.primaryImage}
                        alt={order.primaryProduct}
                        className="w-10 h-10 rounded object-cover"
                      />
                      {order.itemCount > 1 && (
                        <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-medium rounded-full h-4 w-4 flex items-center justify-center">
                          {order.itemCount}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-foreground">#{order.orderNumber}</p>
                      <p className="text-sm text-muted-foreground">{order.primaryProduct}</p>
                    </div>
                  </div>
                </td>

                {/* Date */}
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(order.date)}
                  </span>
                </td>

                {/* Status */}
                <td className="p-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </td>

                {/* Items */}
                <td className="p-4">
                  <span className="text-sm text-muted-foreground">
                    {order.itemCount} item{order.itemCount !== 1 ? 's' : ''}
                  </span>
                </td>

                {/* Total */}
                <td className="p-4">
                  <span className="font-semibold text-foreground">
                    {formatPrice(order.total)}
                  </span>
                </td>

                {/* Actions */}
                <td className="p-4">
                  <div className="flex items-center gap-1">
                    {order.status.toLowerCase() !== 'cancelled' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Package"
                        onClick={() => onTrackOrder(order.id)}
                        className="h-8 w-8 p-0"
                      />
                    )}
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="RotateCcw"
                      onClick={() => onReorder(order.id)}
                      className="h-8 w-8 p-0"
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Download"
                      onClick={() => onDownloadInvoice(order.id)}
                      className="h-8 w-8 p-0"
                    />
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onViewDetails(order.id)}
                      className="h-8 w-8 p-0"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderTable;